/**
 * AudioVariantsTabContent — Audio variant list for the SoundExpert node.
 *
 * Shows a vertical list of audio variant rows, each with:
 * - Waveform bar preview
 * - Version label (v1, v2, ...)
 * - Mode badge (music / speech / sfx)
 * - Duration
 * - Play button for inline preview
 * - Accent border on selected variant
 *
 * Pending variants show spinner placeholders.
 * Variants ordered newest-first.
 */

import { memo, useCallback, useRef, useState } from 'react'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AudioVariant {
  index: number
  audioUrl: string | null
  prompt: string
  mode: string
  durationSeconds?: number
}

export interface AudioVariantsTabContentProps {
  variants: AudioVariant[]
  selectedIndex: number
  batchPending: number
  onSelect: (index: number) => void
}

// ─── Constants ──────────────────────────────────────────────────────────────

const ACCENT = {
  base: '#06b6d4',
  bg20: 'rgba(6, 182, 212, 0.2)',
  bg40: 'rgba(6, 182, 212, 0.4)',
}

const MINI_BAR_COUNT = 20

/** Format seconds as mm:ss */
export function formatMMSS(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '00:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// ─── Audio Variant Row ──────────────────────────────────────────────────────

interface VariantRowProps {
  variant: AudioVariant
  isSelected: boolean
  onSelect: () => void
}

const VariantRow = memo(function VariantRow({
  variant,
  isSelected,
  onSelect,
}: VariantRowProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const togglePlay = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      const el = audioRef.current
      if (!el || !variant.audioUrl) return
      if (isPlaying) {
        el.pause()
      } else {
        el.currentTime = 0
        el.play()
      }
    },
    [isPlaying, variant.audioUrl]
  )

  // Generate static mini-waveform bars (deterministic per variant index)
  const miniBars = Array.from({ length: MINI_BAR_COUNT }, (_, i) => {
    const seed = (variant.index * 7 + i * 13) % 100
    return 20 + (seed / 100) * 80
  })

  return (
    <div
      onClick={onSelect}
      className={`px-3 py-2.5 flex items-center gap-2.5 cursor-pointer transition-colors ${
        isSelected
          ? 'bg-cyan-500/10'
          : 'hover:bg-surface-1/50'
      }`}
      style={
        isSelected
          ? { borderLeft: `3px solid ${ACCENT.base}` }
          : { borderLeft: '3px solid transparent' }
      }
      data-testid={`variant-row-${variant.index}`}
    >
      {/* Play button */}
      <button
        onClick={togglePlay}
        disabled={!variant.audioUrl}
        className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center transition-colors disabled:opacity-30"
        style={{
          background: ACCENT.bg20,
          border: `1px solid ${ACCENT.bg40}`,
        }}
        data-testid={`variant-play-${variant.index}`}
      >
        {isPlaying ? (
          <svg className="w-3 h-3" fill={ACCENT.base} viewBox="0 0 24 24">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg className="w-3 h-3 ml-0.5" fill={ACCENT.base} viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {/* Mini waveform */}
      <div className="flex items-end gap-px h-5 flex-shrink-0" data-testid="variant-waveform">
        {miniBars.map((height, i) => (
          <div
            key={i}
            className="rounded-[0.5px]"
            style={{
              width: 2,
              height: `${height}%`,
              background: ACCENT.base,
              opacity: isSelected ? 0.6 : 0.25,
            }}
          />
        ))}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 flex items-center gap-1.5">
        <span className="text-xxs font-medium text-foreground">
          v{variant.index + 1}
        </span>
        <span
          className="text-xxs px-1.5 py-0.5 rounded-full capitalize"
          style={{ background: ACCENT.bg20, color: ACCENT.base }}
          data-testid={`variant-mode-badge-${variant.index}`}
        >
          {variant.mode}
        </span>
        {variant.durationSeconds !== undefined && (
          <span className="text-xxs text-text-muted font-mono tabular-nums">
            {formatMMSS(variant.durationSeconds)}
          </span>
        )}
      </div>

      {/* Hidden audio element */}
      {variant.audioUrl && (
        <audio
          ref={audioRef}
          src={variant.audioUrl}
          preload="none"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />
      )}
    </div>
  )
})

VariantRow.displayName = 'VariantRow'

// ─── Pending Spinner Row ────────────────────────────────────────────────────

const PendingRow = memo(function PendingRow({ index }: { index: number }) {
  return (
    <div
      className="px-3 py-2.5 flex items-center gap-2.5"
      style={{ borderLeft: '3px solid transparent' }}
      data-testid={`variant-pending-${index}`}
    >
      <div
        className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin flex-shrink-0"
        style={{ borderColor: ACCENT.base, borderTopColor: 'transparent' }}
      />
      <span className="text-xxs text-text-muted">Generating...</span>
    </div>
  )
})

PendingRow.displayName = 'PendingRow'

// ─── Main Component ─────────────────────────────────────────────────────────

const AudioVariantsTabContent = memo(function AudioVariantsTabContent({
  variants,
  selectedIndex,
  batchPending,
  onSelect,
}: AudioVariantsTabContentProps) {
  // Newest-first ordering
  const sortedVariants = [...variants].reverse()
  const hasPending = batchPending > 0

  if (sortedVariants.length === 0 && !hasPending) {
    return (
      <div
        className="h-full flex items-center justify-center"
        data-testid="audio-variants-empty"
      >
        <span className="text-xs text-text-muted">No audio variants yet</span>
      </div>
    )
  }

  return (
    <div
      className="h-full overflow-y-auto nowheel"
      data-testid="audio-variants-tab"
    >
      {/* Pending spinners at the top (newest-first) */}
      {hasPending &&
        Array.from({ length: batchPending }, (_, i) => (
          <PendingRow key={`pending-${i}`} index={i} />
        ))}

      {/* Variant rows */}
      {sortedVariants.map((variant) => (
        <VariantRow
          key={variant.index}
          variant={variant}
          isSelected={variant.index === selectedIndex}
          onSelect={() => onSelect(variant.index)}
        />
      ))}
    </div>
  )
})

AudioVariantsTabContent.displayName = 'AudioVariantsTabContent'

export { AudioVariantsTabContent }
